import React from 'react';
import {
  Avatar,
  Badge,
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  EmojiEvents as LeagueIcon,
  HowToReg as RegisterIcon,
  CalendarMonth as AvailabilityIcon,
  AdminPanelSettings as AdminIcon,
  Dashboard as DashboardIcon,
  People as MembersIcon,
  Logout as LogoutIcon,
  LocationOn as VenuesIcon,
  Event as EventIcon,
  Celebration as SocialEventIcon,
  Assessment as ReportsIcon,
  Groups as MyTeamIcon,
  Description as MatchReportIcon,
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import LoginButton from '../auth/LoginButton';
import LogoutButton from '../auth/LogoutButton';

const MobileDrawer = ({
  open,
  onClose,
  isAuthenticated,
  user,
  currentUserTenantId,
  isAdminMode,
  canAccessAdmin,
  effectiveOrgId,
  availabilityLoading,
  onAvailabilityClick,
  isNavActive,
  myTeams = [],
  canManageAnyTeam = false,
  canViewSocialEvents = false,
  pendingReportsCount = 0,
  conflictCount = 0,
}) => {
  const handleItemClick = () => onClose();
  const location = useLocation();
  const currentTeamSlug = location.pathname.startsWith('/teams/') ? location.pathname.split('/')[2] : null;

  // Determine global nav items based on mode
  const getGlobalItems = () => {
    if (isAdminMode && canAccessAdmin) {
      const items = [
        { label: 'Dashboard', path: '/admin', icon: <DashboardIcon />, value: 'admin-dashboard' },
        { label: 'Members', path: '/admin/members', icon: <MembersIcon />, value: 'admin-members' },
        { label: 'Leagues', path: '/admin/leagues', icon: <LeagueIcon />, value: 'admin-leagues', badgeCount: conflictCount },
        { label: 'Venues', path: '/admin/venues', icon: <VenuesIcon />, value: 'admin-venues' },
        { label: 'Events', path: '/admin/events', icon: <EventIcon />, value: 'admin-events' },
      ];
      if (canViewSocialEvents) {
        items.push({ label: 'Socials', path: '/admin/social-events', icon: <SocialEventIcon />, value: 'admin-social-events' });
      }
      items.push({ label: 'Reports', path: '/admin/reports', icon: <ReportsIcon />, value: 'admin-reports' });
      return items;
    }
    return [
      { label: 'Leagues', path: '/', icon: <LeagueIcon />, value: 'leagues' },
      { label: 'Register', path: '/register', icon: <RegisterIcon />, value: 'register' },
    ];
  };

  const globalItems = getGlobalItems();

  return (
    <Drawer
      id="global-navigation-drawer"
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: { sx: { width: 280 } },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Menu
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="Close menu">
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />

      {/* User info (authenticated) */}
      {isAuthenticated && user && (
        <>
          <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              src={user?.picture}
              alt={user?.name}
              sx={{ width: 36, height: 36 }}
              component={currentUserTenantId ? Link : 'div'}
              to={currentUserTenantId ? `/player/${currentUserTenantId}` : undefined}
              onClick={currentUserTenantId ? handleItemClick : undefined}
              style={{ textDecoration: 'none', cursor: currentUserTenantId ? 'pointer' : 'default' }}
            >
              {user?.name?.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {user?.name}
              </Typography>
              <Chip
                label={user?.role || 'player'}
                size="small"
                variant="outlined"
                clickable={false}
                onClick={() => {}}
                sx={{ fontSize: '0.65rem', height: '18px' }}
              />
            </Box>
          </Box>
          <Divider />
        </>
      )}

      {/* Navigation items */}
      <List sx={{ flex: 1 }}>
        {globalItems.map((item) => (
          <ListItem key={item.value} disablePadding>
            <ListItemButton
              component={Link}
              to={item.path}
              onClick={handleItemClick}
              selected={isNavActive(item.value)}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {item.badgeCount > 0 ? (
                  <Badge badgeContent={item.badgeCount} color="error" max={99}>
                    {item.icon}
                  </Badge>
                ) : (
                  item.icon
                )}
              </ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
        {/* My Team (authenticated, non-admin, has teams) */}
        {isAuthenticated && !isAdminMode && myTeams.length === 1 && (
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to={`/teams/${myTeams[0].teamSlug}`}
              onClick={handleItemClick}
              selected={isNavActive('my-team')}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <MyTeamIcon />
              </ListItemIcon>
              <ListItemText primary="My Team" />
            </ListItemButton>
          </ListItem>
        )}
        {isAuthenticated && !isAdminMode && myTeams.length > 1 && (
          myTeams.map((team) => (
            <ListItem key={team.teamSlug} disablePadding>
              <ListItemButton
                component={Link}
                to={`/teams/${team.teamSlug}`}
                onClick={handleItemClick}
                selected={currentTeamSlug === team.teamSlug}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <MyTeamIcon />
                </ListItemIcon>
                <ListItemText primary={team.teamName} secondary={team.leagueName} />
              </ListItemButton>
            </ListItem>
          ))
        )}
        {/* My Availability (authenticated, non-admin) */}
        {isAuthenticated && !isAdminMode && (
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => { handleItemClick(); onAvailabilityClick(); }}
              disabled={availabilityLoading}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <AvailabilityIcon />
              </ListItemIcon>
              <ListItemText primary="My Availability" />
            </ListItemButton>
          </ListItem>
        )}
        {/* Match Reports (authenticated, non-admin, manages teams) */}
        {isAuthenticated && !isAdminMode && canManageAnyTeam && (
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/match-reports/pending"
              onClick={handleItemClick}
              selected={isNavActive('match-reports')}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Badge badgeContent={pendingReportsCount} color="error" max={99}>
                  <MatchReportIcon />
                </Badge>
              </ListItemIcon>
              <ListItemText primary="Match Reports" />
            </ListItemButton>
          </ListItem>
        )}
      </List>

      <Divider />

      {/* Bottom actions */}
      <List>
        {/* Admin toggle */}
        {isAuthenticated && !isAdminMode && canAccessAdmin && (
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/admin"
              onClick={handleItemClick}
              sx={{ color: 'primary.main' }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <AdminIcon sx={{ color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 500 }}>
                    Admin Dashboard
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
        )}
        {isAuthenticated && isAdminMode && (
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to={effectiveOrgId ? `/organization/${effectiveOrgId}` : '/'}
              onClick={handleItemClick}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <AdminIcon />
              </ListItemIcon>
              <ListItemText primary="Exit Admin" />
            </ListItemButton>
          </ListItem>
        )}

        {/* Auth action */}
        {isAuthenticated ? (
          <ListItem disablePadding onClick={handleItemClick}>
            <LogoutButton
              variant="text"
              size="small"
              startIcon={<LogoutIcon />}
              sx={{
                color: 'inherit',
                border: 'none',
                width: '100%',
                justifyContent: 'flex-start',
                px: 2,
                py: 1,
                '&:hover': { backgroundColor: 'action.hover' },
              }}
            />
          </ListItem>
        ) : (
          <ListItem disablePadding sx={{ px: 2, py: 1 }}>
            <LoginButton
              variant="contained"
              color="primary"
              fullWidth
              sx={{ textTransform: 'none', fontWeight: 500 }}
            >
              Sign In
            </LoginButton>
          </ListItem>
        )}
      </List>
    </Drawer>
  );
};

export default MobileDrawer;
